<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { computed, useAttrs } from "vue"
import { Check, ChevronDown } from "@lucide/vue"
import {
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui"
import { cn } from "@/shared/lib/utils"

const props = defineProps<{
  modelValue: string | null
  options: { value: string; label: string }[]
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  /** Відкрити дропдаун при монтуванні (uncontrolled — для styleguide/dev). */
  defaultOpen?: boolean
  id?: string
  ariaLabel?: string
  ariaLabelledby?: string
  ariaDescribedby?: string
  class?: HTMLAttributes["class"]
}>()

defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  "update:modelValue": [value: string | null]
}>()

const attrs = useAttrs()

const ariaInvalid = computed<true | undefined>(() => {
  if (props.invalid) return true
  const a = attrs["aria-invalid"]
  return a === true || a === "" || a === "true" ? true : undefined
})
</script>

<template>
  <SelectRoot
    :model-value="modelValue ?? undefined"
    :disabled="disabled"
    :default-open="defaultOpen"
    @update:model-value="emit('update:modelValue', ($event as string | undefined) ?? null)"
  >
    <SelectTrigger
      data-slot="select-trigger"
      :id="id"
      :aria-label="props.ariaLabel"
      :aria-labelledby="props.ariaLabelledby"
      :aria-describedby="props.ariaDescribedby"
      :aria-invalid="ariaInvalid"
      :data-invalid="ariaInvalid"
      :class="cn(
        'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm outline-none',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        props.class,
      )"
    >
      <span class="min-w-0 flex-1 truncate text-left">
        <SelectValue :placeholder="placeholder" />
      </span>
      <SelectIcon as-child>
        <ChevronDown class="size-4 shrink-0 opacity-50" />
      </SelectIcon>
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="relative z-50 max-h-60 min-w-[var(--reka-select-trigger-width)] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        position="popper"
        :side-offset="4"
      >
        <SelectViewport class="overflow-y-auto p-1">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :class="cn(
              'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
              'focus:bg-accent focus:text-accent-foreground',
              'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            )"
          >
            <span class="absolute left-2 flex size-3.5 items-center justify-center">
              <SelectItemIndicator>
                <Check class="size-4" />
              </SelectItemIndicator>
            </span>
            <SelectItemText>{{ option.label }}</SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
